'use client';

import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { QuotationStatus } from '@ravago/shared/page-data/models/features/product-quotation.model';
import { QuotationGroup } from '@ravago/shared/page-elements-radiance/utils/my-products-and-prices/my-products-and-prices.utils';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Paper } from '@ravago/shared/radiance/components/paper/components/Paper/Paper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../../hooks/use-path-locale';
import { SystemPageLink } from '../../system-page-link/system-page-link';

interface Props {
   quotation: QuotationGroup;
   quoteDetailBaseRoute: string | null;
}

const StatusBadge = ({ status }: { status: QuotationStatus }) => {
   const translate = useTranslations();
   if (status === 'ACTIVE') return <Badge variant="success">{translate('prices.quote-active')}</Badge>;
   return <Badge variant="error">{translate('prices.quote-expired')}</Badge>;
};

export const ProductPriceCardV2Quotation = ({ quotation, quoteDetailBaseRoute }: Props) => {
   const locale = usePathLocale();
   const translate = useTranslations();
   const format = useFormatter();

   const priceLabel = quotation.maxUnitPrice
      ? `${format.unitPrice(quotation.minUnitPrice)} – ${format.unitPrice(quotation.maxUnitPrice)}`
      : format.unitPrice(quotation.minUnitPrice);
   const priceLabelWithFixedPriceMarker = quotation.isPriceFixed === false ? `${priceLabel}*` : priceLabel;

   return (
      <Paper>
         <BreakpointSwitch excludeViews={['phone']}>
            <Box justify="between" items="center">
               <Box direction="column" gap="xs">
                  <Box gap="sm">
                     {quoteDetailBaseRoute ? (
                        <SystemPageLink
                           baseRoute={quoteDetailBaseRoute}
                           locale={locale}
                           type={SystemPageType.QUOTE_DETAIL}
                           slug={quotation.id}
                           underline="always"
                           fullWidth={false}
                        >
                           <Typography variant="body-highlight" color="inherit">
                              {quotation.name}
                           </Typography>
                        </SystemPageLink>
                     ) : (
                        <Typography variant="body-highlight">{quotation.name}</Typography>
                     )}
                     <StatusBadge status={quotation.status} />
                  </Box>
                  <Box gap="xs">
                     <Typography variant="small" color="subtle">
                        {priceLabelWithFixedPriceMarker}
                     </Typography>
                     {quotation.packaging && (
                        <>
                           <Divider orientation="vertical" selfStretch />
                           <Typography variant="small" color="subtle">
                              {quotation.packaging}
                           </Typography>
                        </>
                     )}
                  </Box>
               </Box>
               {quoteDetailBaseRoute && (
                  <SystemPageLink
                     baseRoute={quoteDetailBaseRoute}
                     locale={locale}
                     type={SystemPageType.QUOTE_DETAIL}
                     slug={quotation.id}
                     underline="none"
                     fullWidth={false}
                  >
                     <Button variant="secondary">{translate('prices.open')}</Button>
                  </SystemPageLink>
               )}
            </Box>
         </BreakpointSwitch>
         <BreakpointSwitch includeViews={['phone']}>
            <Box justify="between" items="center">
               <Box direction="column" justify="between" width="100%">
                  <Box justify="between">
                     {quoteDetailBaseRoute ? (
                        <SystemPageLink
                           baseRoute={quoteDetailBaseRoute}
                           locale={locale}
                           type={SystemPageType.QUOTE_DETAIL}
                           slug={quotation.id}
                           underline="always"
                           fullWidth={false}
                        >
                           <Typography variant="body-highlight" color="inherit">
                              {quotation.name}
                           </Typography>
                        </SystemPageLink>
                     ) : (
                        <Typography variant="body-highlight">{quotation.name}</Typography>
                     )}
                     <StatusBadge status={quotation.status} />
                  </Box>
                  <Box items="center" justify="between" gap="xs">
                     <Box direction="column" gap="xs">
                        <Typography variant="small" color="subtle">
                           {priceLabelWithFixedPriceMarker}
                        </Typography>
                        {quotation.packaging && (
                           <Typography variant="small" color="subtle">
                              {quotation.packaging}
                           </Typography>
                        )}
                     </Box>
                     {quoteDetailBaseRoute && (
                        <SystemPageLink
                           baseRoute={quoteDetailBaseRoute}
                           locale={locale}
                           type={SystemPageType.QUOTE_DETAIL}
                           slug={quotation.id}
                           underline="none"
                           fullWidth={false}
                        >
                           <Button variant="secondary">{translate('prices.open')}</Button>
                        </SystemPageLink>
                     )}
                  </Box>
               </Box>
            </Box>
         </BreakpointSwitch>
      </Paper>
   );
};
